import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Empty } from "@/components/ui-bits";
import { fmtINR, fmtNum } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Trophy, Medal, Target, TrendUp, ClipboardText, Flag, PencilSimple } from "@phosphor-icons/react";

const PERIODS = [
  { key: "today", label: "Today" },
  { key: "week", label: "This Week" },
  { key: "month", label: "This Month" },
  { key: "all", label: "All Time" },
];

const RANK_STYLE = {
  1: "bg-amber-100 text-amber-800 border-amber-400",
  2: "bg-slate-100 text-slate-700 border-slate-300",
  3: "bg-orange-100 text-orange-800 border-orange-300",
};

export default function Leaderboard() {
  const { user } = useAuth();
  const [period, setPeriod] = useState("month");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editAgent, setEditAgent] = useState(null);
  const canManage = ["super_admin", "client_admin", "manager"].includes(user.role);

  const load = () => {
    setLoading(true);
    api.get("/leaderboard", { params: { period } }).then((r) => setRows(r.data.rows)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [period]);

  const podium = rows.slice(0, 3);

  return (
    <div data-testid="leaderboard-page">
      <PageHeader title="Agent Leaderboard" subtitle="Ranked by recovery, kept-promises, activity & closures" />

      {/* Period tabs */}
      <div className="flex flex-wrap gap-2 mb-5">
        {PERIODS.map((p) => (
          <button
            key={p.key}
            onClick={() => setPeriod(p.key)}
            data-testid={`period-${p.key}`}
            className={`px-3 py-1.5 text-sm rounded-sm border transition-colors ${period === p.key ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border hover:bg-secondary"}`}
          >
            {p.label}
          </button>
        ))}
      </div>

      {/* Podium */}
      {!loading && podium.length > 0 && (
        <div className="grid sm:grid-cols-3 gap-3 mb-6" data-testid="leaderboard-podium">
          {podium.map((r) => (
            <div key={r.agent_id} className={`border p-4 rounded-sm ${RANK_STYLE[r.rank] || "border-border bg-card"}`} data-testid={`podium-${r.rank}`}>
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-1.5 font-heading font-bold">
                  {r.rank === 1 ? <Trophy size={18} weight="fill" /> : <Medal size={18} weight="fill" />} #{r.rank}
                </span>
                <span className="text-2xl font-black font-heading mono">{r.score}</span>
              </div>
              <div className="font-semibold mt-2 truncate">{r.agent}</div>
              <div className="text-sm mono mt-1">{fmtINR(r.recovered)} recovered</div>
              <div className="text-xs mt-0.5 opacity-80">{r.ptp_kept_rate}% PTP kept • {fmtNum(r.activities)} activities</div>
            </div>
          ))}
        </div>
      )}

      {/* Table */}
      <div className="border border-border bg-card overflow-x-auto">
        {loading && <div className="label-caps p-6 text-center">Loading…</div>}
        {!loading && rows.length === 0 && <Empty text="No agent activity for this period" />}
        {!loading && rows.length > 0 && (
          <table className="w-full text-sm">
            <thead>
              <tr className="label-caps border-b border-border">
                <th className="text-left p-3 w-12">Rank</th>
                <th className="text-left p-3">Agent</th>
                <th className="text-right p-3"><span className="inline-flex items-center gap-1 justify-end"><TrendUp size={13} /> Recovered</span></th>
                <th className="text-right p-3"><span className="inline-flex items-center gap-1 justify-end"><Flag size={13} /> PTP Kept</span></th>
                <th className="text-right p-3"><span className="inline-flex items-center gap-1 justify-end"><ClipboardText size={13} /> Activities</span></th>
                <th className="text-right p-3">Closures</th>
                {period === "month" && <th className="text-right p-3"><span className="inline-flex items-center gap-1 justify-end"><Target size={13} /> Target</span></th>}
                <th className="text-right p-3">Score</th>
                {canManage && <th className="text-right p-3"></th>}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.agent_id} className="border-b border-border last:border-0 hover:bg-secondary/40" data-testid={`lb-row-${r.agent_id}`}>
                  <td className="p-3">
                    <span className={`inline-flex items-center justify-center h-7 w-7 rounded-sm border text-xs font-black mono ${RANK_STYLE[r.rank] || "border-border bg-secondary"}`}>{r.rank}</span>
                  </td>
                  <td className="p-3 font-medium">{r.agent}</td>
                  <td className="p-3 text-right mono font-semibold text-emerald-600">{fmtINR(r.recovered)}</td>
                  <td className="p-3 text-right mono">{r.ptp_kept}/{r.ptp_total} <span className="text-xs text-muted-foreground">({r.ptp_kept_rate}%)</span></td>
                  <td className="p-3 text-right mono">{fmtNum(r.activities)}</td>
                  <td className="p-3 text-right mono">{fmtNum(r.closures)}</td>
                  {period === "month" && (
                    <td className="p-3 text-right mono">
                      {r.monthly_recovery_target > 0 ? (
                        <span className={r.target_attainment >= 100 ? "text-emerald-600 font-semibold" : "text-muted-foreground"}>{r.target_attainment}%</span>
                      ) : <span className="text-muted-foreground text-xs">—</span>}
                    </td>
                  )}
                  <td className="p-3 text-right"><span className="inline-block px-2 py-0.5 rounded-sm bg-primary/10 text-primary font-black mono">{r.score}</span></td>
                  {canManage && (
                    <td className="p-3 text-right">
                      <button onClick={() => setEditAgent(r)} className="text-muted-foreground hover:text-foreground" title="Set monthly target" data-testid={`set-target-${r.agent_id}`}>
                        <PencilSimple size={15} weight="bold" />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {editAgent && <TargetDialog agent={editAgent} onClose={() => setEditAgent(null)} onSaved={() => { setEditAgent(null); load(); }} />}
    </div>
  );
}

function TargetDialog({ agent, onClose, onSaved }) {
  const [val, setVal] = useState(agent.monthly_recovery_target || "");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/agents/${agent.agent_id}/target`, { monthly_recovery_target: +val || 0 });
      toast.success("Target updated");
      onSaved();
    } catch (e) { toast.error("Failed to update target"); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-sm" data-testid="target-dialog">
        <DialogHeader>
          <DialogTitle>Monthly Recovery Target</DialogTitle>
          <DialogDescription>Set {agent.agent}'s monthly collection target (₹). Attainment shows on the leaderboard.</DialogDescription>
        </DialogHeader>
        <div>
          <Label className="text-xs">Target Amount (₹)</Label>
          <Input type="number" className="rounded-sm" value={val} onChange={(e) => setVal(e.target.value)} placeholder="e.g. 200000" data-testid="target-input" />
        </div>
        <DialogFooter><Button onClick={save} disabled={saving} className="rounded-sm" data-testid="target-save">{saving ? "Saving…" : "Save Target"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
